import React, { useContext, useCallback, useEffect, useRef, useMemo } from 'react';
import moment from 'moment/moment';
import produce, { Draft } from 'immer';
import CircularProgress from '@material-ui/core/CircularProgress';
import { fetchUserMessageLogEntries, ritzDataContext, selectProjectById, selectUserMessageLogs } from '@moderepo/mode-data-state-ritz';
import { PaginationBar, getMidnightDate, useSearchParams, capitalizeAll, ITEMS_PER_PAGE_LIST, PAGING_MIN_PAGES, BaseListCompField,
    SelectInputOption, Time, ITEMS_PER_PAGE_100, GenericMessageBox } from '@moderepo/mode-app-components';
import { LogsLookup, MessageLogsTable, MessageLogsTableProps } from '@moderepo/mode-app-components-internal';
import { SortOrder } from '@moderepo/mode-apis';
import { useRitzTranslation } from '../../utils/customHooks';
import { createLogRequestIntervalOptions, localizeMessageLogEntries, sortMessageLogEntries } from '../containerUtils';


/**
 * Customize search params. For user message logs, we will need additional fields in the search params, start and end date
 */
interface LogSearchParams {
    readonly start: Date;
    readonly interval: number;
    readonly sortField: string;
    readonly sortOrder: SortOrder;
}


/**
 * Because this is a container, not a page, it doesn't and shouldn't have access to the route params. Therefore, the page
 * that display this container must provide these information.
 */
interface UserMessageLogsContainerProps {
    readonly projectId: number;
    readonly userId: number;
}


/**
 * This is the Container that display a user's message logs. It is a SMART component which contains all the logics but
 * no UI.
 */
export const UserMessageLogsContainer: React.FC<UserMessageLogsContainerProps> = (props: UserMessageLogsContainerProps) => {
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);


    // The minDate is the oldest date the user can select which should be the project's creation date.
    const minDate = useRef<Date>(getMidnightDate(moment(project?.creationTime || 0).toDate()));

    // maxDate the latest date the user can select which should be TOMORROW
    const maxDate = useRef<Date>(getMidnightDate(moment(Date.now()).add(1, 'day').toDate()));

    // Initially we will pick a default start date of 1 hour ago
    const initDateRangeStart = moment(Date.now() - Time.HOUR_IN_MS).toDate();

    const {
        searchParams, onOtherFieldsChange, onItemsPerPageChange, onPrevPage, onNextPage, onPage,
    } = useSearchParams<LogSearchParams>({
        searchText : '',
        otherFields: {
            start    : initDateRangeStart,
            interval : Time.HOUR_IN_MS,
            sortField: 'timestamp',
            sortOrder: SortOrder.DESC,
        },
        itemsPerPage: ITEMS_PER_PAGE_100,
        currentPage : 0,
    });


    // This is the complete set of log entries that we have requested
    const logEntries = selectUserMessageLogs(dataState, props.userId, {
        begin: searchParams.otherFields.start.toISOString(),
        end  : (new Date(searchParams.otherFields.start.valueOf() + searchParams.otherFields.interval)).toISOString(),
    });

    // sort the log entries by the current sort field and order
    const sortedLogEntries = sortMessageLogEntries(logEntries, searchParams.otherFields.sortField, searchParams.otherFields.sortOrder);

    // LogEntries contain all the entries from the search result. Backend does not support paging so we need to handle
    // paging in the frontend. This list contains only the entries for the current selected page.
    const filteredLogEntries = sortedLogEntries
        ? sortedLogEntries.slice(searchParams.itemsPerPage * searchParams.currentPage, searchParams.itemsPerPage * (searchParams.currentPage + 1))
        : undefined;

    // Pre-translated the log entries data before we pass it to the MessageLogsTableComp because MessageLogsComponents
    // does not know how to localize the data.
    const translatedFilteredLogEntries = localizeMessageLogEntries(filteredLogEntries, trans);


    /**
     * Load log entries base on the filters from the current searchParams
     */
    const loadLogEntries = useCallback(async (userId: number, start: Date, interval: number) => {
        try {
            await dataDispatch(fetchUserMessageLogEntries(
                userId, start.toISOString(), (new Date(start.valueOf() + interval)).toISOString(),
            ));
        } catch (error) {
            /* ignore error */
        }
    }, [dataDispatch]);



    /**
     * This useEffect is used for loading log entries when one of the search params changed
     */
    useEffect(() => {
        if (searchParams.otherFields.start && searchParams.otherFields.interval) {
            loadLogEntries(props.userId, searchParams.otherFields.start, searchParams.otherFields.interval);
        }
    }, [loadLogEntries, props.userId, searchParams.otherFields.start, searchParams.otherFields.interval]);



    const onStartDateChanged = useCallback((date: Date) => {
        if (searchParams.otherFields) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<LogSearchParams>) => {
                draft.start = date;
            }));
        }
    }, [onOtherFieldsChange, searchParams.otherFields]);


    const onIntervalChanged = useCallback((value: number) => {
        if (searchParams.otherFields) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<LogSearchParams>) => {
                draft.interval = value;
            }));
        }
    }, [onOtherFieldsChange, searchParams.otherFields]);



    /**
     * On column clicked, change the sort order/field
     */
    const onSortByField = useCallback((field: BaseListCompField) => {
        if (searchParams.otherFields) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<LogSearchParams>) => {

                if (draft.sortField === field.dataItemProp) {
                    // this column is currently being sorted, change the toggle the sort order instead
                    if (draft.sortOrder === SortOrder.ASC) {
                        draft.sortOrder = SortOrder.DESC;
                    } else {
                        draft.sortOrder = SortOrder.ASC;
                    }
                } else if (field.dataItemProp) {
                    // This column is not the current column being sorted, change it to this column and
                    draft.sortField = field.dataItemProp;
                }
            }));
        }
    }, [searchParams, onOtherFieldsChange]);


    /**
     * Create a list of Interval options to be used for the Interval Select dropdown
     */
    const logRequestIntervalOptions = useMemo((): readonly SelectInputOption<number>[] => {
        return createLogRequestIntervalOptions(trans);
    }, [trans]);


    return (
        <>
            {project && (
                <>
                    {searchParams && searchParams.otherFields && (
                        <LogsLookup<MessageLogsTableProps>
                            dates={{
                                texts: {
                                    today : capitalizeAll(trans('label.today')),
                                    ok    : capitalizeAll(trans('label.ok')),
                                    cancel: capitalizeAll(trans('label.cancel')),
                                },
                                start: {
                                    label  : capitalizeAll(trans('label.from')),
                                    date   : searchParams.otherFields.start,
                                    minDate: minDate.current,
                                    maxDate: maxDate.current,
                                },
                                interval: {
                                    label  : capitalizeAll(trans('label.interval')),
                                    options: logRequestIntervalOptions,
                                    value  : searchParams.otherFields.interval,
                                },
                                previousNextButton: {
                                    previousButtonLabel: capitalizeAll(trans('label.prev')),
                                    nextButtonLabel    : capitalizeAll(trans('label.next')),
                                },
                                onStartDateChanged,
                                onIntervalChanged,
                            }}
                            logEntriesLoaded={filteredLogEntries !== undefined}
                            hasSearchResult={filteredLogEntries !== undefined && filteredLogEntries.length > 0}
                            logDisplayComp={MessageLogsTable}
                            logDisplayCompProps={{
                                fieldsSettings: {
                                    fields: {
                                        level: {
                                            dataItemProp: 'level',
                                            label       : capitalizeAll(trans('label.log_level')),
                                            sortable    : logEntries && logEntries.length > 1,
                                        },
                                        message: {
                                            dataItemProp: 'message',
                                            label       : capitalizeAll(trans('label.message')),
                                        },
                                        caller: {
                                            dataItemProp: 'caller',
                                            label       : capitalizeAll(trans('label.caller')),
                                        },
                                        tags: {
                                            dataItemProp: 'tags',
                                            label       : capitalizeAll(trans('label.tag', { count: 2 })),
                                            hidden      : true,
                                        },
                                        sudoer: {
                                            dataItemProp: 'sudoer',
                                            label       : capitalizeAll(trans('label.sudoer')),
                                            sortable    : logEntries && logEntries.length > 1,
                                            hidden      : true,
                                        },
                                        timestamp: {
                                            dataItemProp: 'timestamp',
                                            label       : capitalizeAll(trans('label.timestamp')),
                                            sortable    : logEntries && logEntries.length > 1,
                                        },
                                    },

                                    onSortByField,
                                    currentSortedField: searchParams.otherFields ? searchParams.otherFields.sortField : undefined,
                                    currentSortedOrder: searchParams.otherFields ? searchParams.otherFields.sortOrder : undefined,
                                },
                                listData: translatedFilteredLogEntries || [],
                            }}
                            paginationComp={(
                                <PaginationBar {...{
                                    itemsPerPageList: ITEMS_PER_PAGE_LIST,
                                    itemsPerPage    : searchParams.itemsPerPage,
                                    pagingMinPages  : PAGING_MIN_PAGES,
                                    currentPage     : searchParams.currentPage,
                                    totalItems      : logEntries ? logEntries.length : undefined,
                                    hasMoreItems    : logEntries !== undefined
                                                && logEntries.length > ((searchParams.currentPage + 1) * searchParams.itemsPerPage),
                                    texts: {
                                        itemsPerPage: capitalizeAll(trans('label.paging.items_per_page', {
                                            item_name: trans('label.entry', { count: 2 }),
                                        })),
                                        pageNTemplate   : capitalizeAll(trans('label.page_n')),
                                        pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                                    },
                                    onItemsPerPageChange,
                                    onNextPage,
                                    onPrevPage,
                                    onPage,
                                }}
                                />
                            )}
                            noDataMessageComp={(
                                <GenericMessageBox
                                    title={trans('generic.no_data.title', {
                                        term: capitalizeAll(trans('label.logs', { count: 2 })),
                                    })}
                                />
                            )}
                            emptyDataMessageComp={(
                                <GenericMessageBox
                                    title={trans('generic.no_data.title', {
                                        term: capitalizeAll(trans('label.log_entry', { count: 2 })),
                                    })}
                                />
                            )}
                        />
                    )}
                </>
            )}

            {(!project) && (
                <CircularProgress />
            )}
        </>
    );
};
